import { Toggle } from '@/components/ui/toggle'
import { WithTooltip } from '@/components/ui/tooltip'
import { cn } from '@/components/ui/utils'
import { useEditorStore } from '@/stores/editorStore'

import { KindIcon } from './KindPicker'
import { TOOLS } from './tools/reducer'
import type { ToolId } from './tools/types'

/**
 * The tool rail (§3.2): an icon, its letter and one plain sentence.
 *
 * The sentence is the point. "Polygon" means nothing to a teacher; "Click around the edge of the
 * thing" means exactly what it says, and it is the same text the shortcut sheet shows, so the two
 * can never drift.
 *
 * A column beside the canvas on wide screens (scrolling inside itself on short ones), a row above
 * it on narrow ones that wraps onto a second line rather than hiding tools past the edge — never a
 * reason for the page (or the row) to scroll sideways.
 */

export interface ToolRailProps {
  className?: string
  /** Tools to show (Simple fix mode shows none of them at all). */
  only?: ToolId[]
}

export function ToolRail({ className, only }: ToolRailProps) {
  const tool = useEditorStore((s) => s.tool)
  const setTool = useEditorStore((s) => s.setTool)
  const list = only ? TOOLS.filter((t) => only.includes(t.id)) : TOOLS

  return (
    <nav
      aria-label="Tools"
      className={cn(
        'flex min-h-0 min-w-0 shrink-0 items-center gap-1 overflow-x-auto overscroll-contain border-line bg-paper p-1.5',
        'flex-row flex-wrap border-b lg:flex-col lg:flex-nowrap lg:overflow-x-visible lg:overflow-y-auto lg:border-b-0 lg:border-r lg:p-2',
        className,
      )}
      data-testid="tool-rail"
    >
      {list.map((t) => {
        const active = tool === t.id
        return (
          <WithTooltip
            key={t.id}
            side="right"
            label={
              <span className="block max-w-56">
                <span className="font-medium">
                  {t.label} <kbd className="ml-1 rounded bg-paper-3 px-1 font-mono text-[10px]">{t.shortcut.toUpperCase()}</kbd>
                </span>
                <span className="mt-0.5 block text-xs opacity-90">{t.hint}</span>
              </span>
            }
          >
            <Toggle
              pressed={active}
              onPressedChange={() => setTool(t.id)}
              aria-label={`${t.label} (${t.shortcut.toUpperCase()}) — ${t.hint}`}
              aria-keyshortcuts={t.shortcut}
              className="relative w-11 shrink-0 flex-col gap-0.5 py-1 lg:w-12"
            >
              <KindIcon name={t.icon} className="size-5" />
              <span className="text-[10px] leading-none opacity-70">{t.shortcut.toUpperCase()}</span>
            </Toggle>
          </WithTooltip>
        )
      })}
    </nav>
  )
}
